import { isObject, isString, type CastNeverToUndefined, type DefaultIfNever } from "@mcwd/typescript-type-guards";
import { tryJsonParse } from "./tryJsonParse.js";
import { parseError } from "./parseError.js";

let localStorageIsAvailable: boolean | null = null;
export function checkLocalStorageAvailable(): boolean {
  if (localStorageIsAvailable == null) {
    let storage: typeof window[ "localStorage" ] | null = null;
    try {
      storage = window.localStorage;
      const x = "__storage_test__";
      storage.setItem(x, x);
      storage.removeItem(x);
      localStorageIsAvailable = true;
    } catch (e) {
      if (isObject(e) && e instanceof DOMException && e.name === "QuotaExceededError") {
        // acknowledge QuotaExceededError only if there's something already stored
        if (storage && storage.length !== 0) {
          localStorageIsAvailable = true;
        }
      }
      else {
        localStorageIsAvailable = false;
      }
    }
  }
  return localStorageIsAvailable === true;
}

export function readRawValueFromLocalStorage<ThrowOnError extends boolean = false>(key: string, { throwOnError }: { throwOnError?: ThrowOnError } = {}): string | null | undefined | (ThrowOnError extends false? Error : never) {
  type ThisReturnType = string | null | undefined | (ThrowOnError extends false ? Error : never);
  try
  {
    if (checkLocalStorageAvailable()) {
      return window.localStorage.getItem(key);
    }
    return undefined;
  }
  catch (e)
  {
    const err = parseError(e, { defaultMessage: "Error encountered while reading from localStorage!" });
    if (throwOnError){
      throw err;
    }
    return err as ThisReturnType;
  }
}


export function readFromLocalStorage<
  T = never,
  TDefault = never
> (
  key: string,
  opts: { 
    defaultValue?: TDefault, 
    parseFn?: ((value: string) => T),
    throwOnError?: boolean
  } = {}
): DefaultIfNever<T, unknown> | CastNeverToUndefined<TDefault>
{
  const throwOnError = opts.throwOnError ?? false;
  const defaultValue = (("defaultValue" in opts) ? opts.defaultValue : undefined) as CastNeverToUndefined<TDefault>;
  try {
    const parseFn = (opts.parseFn ?? tryJsonParse) as ((val: unknown) => DefaultIfNever<T, unknown>);
    const rawValue = readRawValueFromLocalStorage(key, { throwOnError });
    if (isString(rawValue)) {
      return parseFn(rawValue);
    }
    return defaultValue;
  }
  catch(err) {
    if (throwOnError){
      throw parseError(err, { callingLabel: "readFromLocalStorage" });
    }
    return defaultValue;
  }
}

export function writeToLocalStorage(key: string, value: unknown, { throwOnError = false } : { throwOnError?: boolean } = {}){
  try {
    if (checkLocalStorageAvailable()) {
      window.localStorage.setItem(key, JSON.stringify(value));
      return true;
    }
    else {
      return false;
    }
  }
  catch (err){
    if (throwOnError){
      throw parseError(err, { callingLabel: "writeToLocalStorage" });
    }
    return false;
  }
}
export function removeFromLocalStorage(key: string, { throwOnError = false }: { throwOnError?: boolean } = {})
{
  try
  {
    if (checkLocalStorageAvailable()) {
      window.localStorage.removeItem(key);
      return true;
    }
  }
  catch (err)
  {
    if (throwOnError) {
      throw parseError(err, { callingLabel: "removeFromLocalStorage" });
    }
  }
  return false;
}